import React from "react";
import { v4 as uuid } from "uuid";

import { usePlayContext } from "../../pages/Play/play-context";
import styles from "./action-lines.module.scss";
import { useActionPath } from "./hooks";
import { ArrowMarker } from "./markers";
import { ActionLineProps } from "./types";

const subdividePath = (path: string, stepSize = 16, startOffset = 0) => {
  const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
  p.setAttribute("d", path);

  const numOfPoints = Math.floor(p.getTotalLength() / stepSize) + 2;
  const indexToStart = Math.floor(startOffset * numOfPoints);
  const points = Array.from(Array(numOfPoints), (_, i) => i)
    .slice(indexToStart)
    .map((i) => p.getPointAtLength(i * stepSize));

  const firstPoint = points[0];
  return `M ${firstPoint?.x || 0} ${firstPoint?.y || 0} ${points
    .slice(1)
    .map(({ x, y }) => `L ${x} ${y}`)
    .join(" ")}`;
};

export interface DottedLineProps extends ActionLineProps {
  classicMarkerHead?: boolean;
}

export const DottedLine = ({
  action,
  controlPoints,
  strokeWidth = 0.3,
  classicMarkerHead = false,
  animateable = false,
}: DottedLineProps) => {
  const { color = "#000" } = action;
  const markerEndId = uuid();

  const { pathsWithScales, endTime } = useActionPath({ action, controlPoints });

  const { animationTimerApi } = usePlayContext();
  const { effectiveTime: time } = animationTimerApi;

  return (
    <>
      <defs>
        <marker
          id={`pass-marker-${markerEndId}`}
          viewBox={`-1 -1 12 12`}
          markerWidth={12}
          markerHeight={12}
          markerUnits="userSpaceOnUse"
          orient="auto"
          fill={color}
        >
          <rect x={-strokeWidth * 3} y={-strokeWidth / 2} width={strokeWidth * 2} height={strokeWidth} />
        </marker>
      </defs>
      <ArrowMarker id={markerEndId} color={color} xOffset={2.5} />

      {pathsWithScales.map((path, i) => (
        <g key={i}>
          {animateable ? (
            <>
              <path
                d={path.pathString}
                {...(i === pathsWithScales.length - 1 && { markerEnd: `url(#arrow-${markerEndId})` })}
                stroke="none"
                fill="none"
                strokeWidth={strokeWidth}
                opacity={animateable && endTime && time >= endTime ? 0.2 : 1}
              />
              <path
                d={subdividePath(path.pathString, 1, path.pathPercentageScale(animateable ? time : 0))}
                markerMid={`url(#pass-marker-${markerEndId})`}
                className={styles.actionLine}
                stroke="transparent"
                strokeWidth={strokeWidth}
              />
              <path
                d={subdividePath(path.pathString, 1)}
                markerMid={`url(#pass-marker-${markerEndId})`}
                className={styles.actionLine}
                stroke="transparent"
                strokeWidth={strokeWidth}
                opacity={0.2}
              />
            </>
          ) : (
            <path
              d={path.pathString}
              className={styles.actionLine}
              markerEnd={`url(#arrow-${markerEndId})`}
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${strokeWidth * 2}, ${strokeWidth}`}
            />
          )}
        </g>
      ))}
    </>
  );
};
